/**
 * Validation and rendering for SubKrewe captain announcement emails.
 *
 * Shared between the composer UI (live validation, preview) and the send
 * endpoint (authoritative validation). The server never trusts the client's
 * copy of any of this — it re-runs `validateSubkreweEmailInput` on the payload
 * it receives.
 *
 * Rendering is the only thing between captain-typed text and a recipient's
 * inbox, so it escapes first and only then adds the small set of tags below.
 */

import type { SubkreweEmailAttachment, SubkreweEmailAudience } from '~/shared/types/subkreweEmail.js'

/**
 * Amazon SES rejects messages over 10 MB *after* base64 encoding, which adds
 * ~37%. Capping the raw total at 6 MB leaves room for the body and headers.
 */
export const SUBKREWE_EMAIL_LIMITS = {
  subjectMax: 200,
  bodyMax: 20000,
  maxAttachments: 5,
  maxAttachmentBytes: 5 * 1024 * 1024,
  maxTotalAttachmentBytes: 6 * 1024 * 1024,
  /** Per-captain cap, counted across a rolling window, to bound abuse. */
  maxSendsPerDay: 10,
} as const

/**
 * Attachment types captains actually need. Deliberately excludes archives and
 * anything executable — those trip spam filters and would put the shared
 * sending reputation (which dues receipts depend on) at risk.
 */
export const ALLOWED_ATTACHMENT_TYPES: readonly string[] = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/heic',
  'text/plain',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]

export const SUBKREWE_EMAIL_AUDIENCES: readonly SubkreweEmailAudience[] = [
  'all',
  'members',
  'redshirts',
  'captains',
  'dues_unpaid',
  'custom',
]

export const APPLE_RELAY_DOMAIN = '@privaterelay.appleid.com'

export function isAppleRelayAddress(email: string | null | undefined): boolean {
  return Boolean(email?.toLowerCase().trim().endsWith(APPLE_RELAY_DOMAIN))
}

export function isAllowedAttachmentType(contentType: string | null | undefined): boolean {
  return ALLOWED_ATTACHMENT_TYPES.includes((contentType ?? '').toLowerCase().trim())
}

/**
 * Strips anything that could be interpreted as a header continuation. Applied
 * to every value that becomes a mail header — a raw newline in a subject or
 * display name is a header-injection primitive.
 */
export function stripHeaderBreaks(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\r\n\u0000-\u001F\u007F]/g, ' ').replace(/\s+/g, ' ').trim()
}

/**
 * Builds the From header. The address is always the krewe's own mailbox — it
 * is the one registered with Apple for Hide My Email delivery, and the only
 * place bounces can actually land. The SubKrewe identity rides in the display
 * name, which is what a recipient's client shows.
 */
export function buildSubkreweFrom(subkreweName: string, fromAddress: string): string {
  const label = stripHeaderBreaks(subkreweName).slice(0, 120)
  const display = label ? `${label} (Chewbacchus)` : 'Intergalactic Krewe of Chewbacchus'
  const quoted = display.replace(/["\\]/g, '\\$&')
  return `"${quoted}" <${stripHeaderBreaks(fromAddress)}>`
}

/** Builds a Reply-To pointing at the captain. */
export function buildSubkreweReplyTo(name: string, email: string): string {
  const label = stripHeaderBreaks(name).slice(0, 120)
  const address = stripHeaderBreaks(email)
  if (!label) return address
  return `"${label.replace(/["\\]/g, '\\$&')}" <${address}>`
}

export interface SubkreweEmailInput {
  subject: string
  body: string
  audience: SubkreweEmailAudience
  memberIds?: string[]
  attachments?: SubkreweEmailAttachment[]
}

/**
 * Returns a list of human-readable problems. Empty means the input is
 * acceptable. Used for both the composer's inline errors and the endpoint's
 * 400 response, so the two can never disagree.
 */
export function validateSubkreweEmailInput(input: SubkreweEmailInput): string[] {
  const errors: string[] = []
  const subject = stripHeaderBreaks(input.subject ?? '')
  const body = (input.body ?? '').trim()

  if (!subject) errors.push('Subject is required.')
  else if (subject.length > SUBKREWE_EMAIL_LIMITS.subjectMax) {
    errors.push(`Subject must be ${SUBKREWE_EMAIL_LIMITS.subjectMax} characters or fewer.`)
  }

  if (!body) errors.push('Message is required.')
  else if (body.length > SUBKREWE_EMAIL_LIMITS.bodyMax) {
    errors.push(`Message must be ${SUBKREWE_EMAIL_LIMITS.bodyMax} characters or fewer.`)
  }

  if (!SUBKREWE_EMAIL_AUDIENCES.includes(input.audience)) {
    errors.push('Choose who should receive this email.')
  }

  if (input.audience === 'custom' && !(input.memberIds?.length)) {
    errors.push('Select at least one recipient.')
  }

  const attachments = input.attachments ?? []
  if (attachments.length > SUBKREWE_EMAIL_LIMITS.maxAttachments) {
    errors.push(`Attach at most ${SUBKREWE_EMAIL_LIMITS.maxAttachments} files.`)
  }

  let total = 0
  for (const file of attachments) {
    total += file.size
    if (!isAllowedAttachmentType(file.contentType)) {
      errors.push(`"${file.filename}" is a file type we can't send.`)
    }
    if (file.size > SUBKREWE_EMAIL_LIMITS.maxAttachmentBytes) {
      errors.push(`"${file.filename}" is larger than ${formatBytes(SUBKREWE_EMAIL_LIMITS.maxAttachmentBytes)}.`)
    }
  }
  if (total > SUBKREWE_EMAIL_LIMITS.maxTotalAttachmentBytes) {
    errors.push(`Attachments total more than ${formatBytes(SUBKREWE_EMAIL_LIMITS.maxTotalAttachmentBytes)}.`)
  }

  return errors
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Trailing punctuation that is almost always sentence structure, not URL. */
const TRAILING_PUNCTUATION = /(?:[.,;:!?)\]]|&quot;|&gt;|&amp;)+$/

/**
 * Escapes captain-typed text and turns it into a small, fixed set of tags:
 * paragraphs, line breaks, and links. Escaping happens first, so the only
 * markup in the output is what this function produced.
 */
export function renderSubkreweEmailBody(body: string): string {
  const paragraphs = escapeHtml(body.replace(/\r\n/g, '\n').trim())
    .split(/\n{2,}/)
    .map(block => block.trim())
    .filter(Boolean)

  return paragraphs
    .map((block) => {
      const linked = block.replace(/(https?:\/\/[^\s<]+)/g, (match) => {
        const trailing = match.match(TRAILING_PUNCTUATION)?.[0] ?? ''
        const url = trailing ? match.slice(0, -trailing.length) : match
        if (!url) return match
        return `<a href="${url}" style="color:#23B00D;">${url}</a>${trailing}`
      })
      return `<p style="margin:0 0 16px 0;font-size:15px;line-height:24px;color:#333333;">${linked.replace(/\n/g, '<br>')}</p>`
    })
    .join('\n')
}

export interface SubkreweEmailRenderContext {
  subkreweName: string
  subject: string
  body: string
  captainName: string
  attachments?: SubkreweEmailAttachment[]
  /** Absolute URL of the member's account page, used in the footer. */
  accountUrl: string
  /**
   * Overrides the "Sent by a captain of X" footer line. Krewe-wide broadcasts
   * pass their own wording — nobody is a captain of the whole krewe.
   */
  senderNote?: string
  /** Overrides the "member of this SubKrewe" line for the same reason. */
  audienceNote?: string
  year?: number
}

/**
 * Full HTML message. Table-based and inline-styled to match the MJML-rendered
 * templates already in the `templates` collection — same palette, same 600px
 * card on #F8FAFF.
 */
export function renderSubkreweEmailHtml(context: SubkreweEmailRenderContext): string {
  const year = context.year ?? new Date().getFullYear()
  const subkreweName = escapeHtml(stripHeaderBreaks(context.subkreweName))
  const subject = escapeHtml(stripHeaderBreaks(context.subject))
  const captainName = escapeHtml(stripHeaderBreaks(context.captainName))
  const accountUrl = escapeHtml(context.accountUrl)
  const attachments = context.attachments ?? []
  const senderNote = escapeHtml(stripHeaderBreaks(
    context.senderNote ?? `Sent by a captain of ${stripHeaderBreaks(context.subkreweName)}. Reply to reach them directly.`,
  ))
  const audienceNote = escapeHtml(stripHeaderBreaks(
    context.audienceNote ?? 'You\'re receiving this because you\'re a member of this SubKrewe.',
  ))

  const attachmentBlock = attachments.length
    ? `
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-top:8px;border-top:1px solid #e5e7eb;">
                <tr>
                  <td style="padding-top:16px;font-family:Courier, monospace;font-size:13px;color:#6b7280;">
                    ${attachments.length === 1 ? 'Attachment:' : 'Attachments:'}
                    ${attachments.map(file => `${escapeHtml(file.filename)} (${formatBytes(file.size)})`).join(' &middot; ')}
                  </td>
                </tr>
              </table>`
    : ''

  return `<!doctype html>
<html lang="en" dir="auto" xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <title>${subject}</title>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <style type="text/css">
      body { margin:0;padding:0;-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%; }
      table, td { border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt; }
      img { border:0;height:auto;line-height:100%;outline:none;text-decoration:none; }
    </style>
  </head>
  <body style="word-spacing:normal;background-color:#F8FAFF;">
    <div style="display:none;font-size:1px;color:#F8FAFF;line-height:1px;max-height:0px;max-width:0px;opacity:0;overflow:hidden;">${subject}</div>
    <div role="article" aria-roledescription="email" aria-label="${subject}" lang="en" dir="auto" style="background-color:#F8FAFF;">
      <table role="presentation" align="center" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:100%;margin:0 auto;">
        <tr>
          <td style="padding:24px 24px 8px 24px;text-align:center;">
            <img src="https://krewebacchus.org/images/krewebacchus-title.png" alt="Intergalactic Krewe of Chewbacchus" width="260" style="width:260px;max-width:100%;">
          </td>
        </tr>
        <tr>
          <td style="padding:0 12px;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;border-radius:8px;">
              <tr>
                <td style="padding:24px 24px 8px 24px;font-family:Courier, monospace;">
                  <div style="font-size:13px;letter-spacing:0.04em;text-transform:uppercase;color:#6b7280;">${subkreweName}</div>
                  <h1 style="margin:8px 0 16px 0;font-size:20px;line-height:28px;color:#333333;">${subject}</h1>
                </td>
              </tr>
              <tr>
                <td style="padding:0 24px 8px 24px;font-family:Courier, monospace;">
${renderSubkreweEmailBody(context.body)}
                  <p style="margin:24px 0 0 0;font-size:15px;line-height:24px;color:#333333;">&mdash; ${captainName}</p>
                </td>
              </tr>
              <tr>
                <td style="padding:16px 24px 24px 24px;font-family:Courier, monospace;">${attachmentBlock}
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding:16px 24px 32px 24px;text-align:center;font-family:Courier, monospace;font-size:12px;line-height:20px;color:#6b7280;">
            <p style="margin:0 0 6px 0;">${senderNote}</p>
            <p style="margin:0 0 6px 0;">${audienceNote} <a href="${accountUrl}" style="color:#6b7280;">Manage your account</a>.</p>
            <p style="margin:0;">&copy; ${year} Intergalactic Krewe of Chewbacchus. All rights reserved.</p>
          </td>
        </tr>
      </table>
    </div>
  </body>
</html>`
}

/** Plain-text alternative. Some relays and filters weight its absence heavily. */
export function renderSubkreweEmailText(context: SubkreweEmailRenderContext): string {
  const attachments = context.attachments ?? []
  const lines = [
    stripHeaderBreaks(context.subkreweName),
    '',
    stripHeaderBreaks(context.subject),
    '',
    context.body.replace(/\r\n/g, '\n').trim(),
    '',
    `— ${stripHeaderBreaks(context.captainName)}`,
  ]

  if (attachments.length) {
    lines.push('', attachments.length === 1 ? 'Attachment:' : 'Attachments:')
    for (const file of attachments) lines.push(`  ${file.filename} (${formatBytes(file.size)})`)
  }

  lines.push(
    '',
    '---',
    context.senderNote ?? `Sent by a captain of ${stripHeaderBreaks(context.subkreweName)}. Reply to reach them directly.`,
    `${context.audienceNote ?? 'You\'re receiving this because you\'re a member of this SubKrewe.'} Manage your account: ${context.accountUrl}`,
  )

  return lines.join('\n')
}

/**
 * The subset of a member record recipient selection needs. `shouldPayDues` is
 * passed in rather than derived here so this stays a pure function — the
 * caller owns the dues rule (see shared/dues/status.ts).
 */
export interface SubkreweEmailCandidate {
  id: string
  name: string
  email: string
  member_type?: 'member' | 'redshirt' | 'captain' | 'pending'
  shouldPayDues: boolean
  /**
   * Address SES told us is dead or that its owner marked as spam. Excluded
   * from every audience — continuing to mail these is what degrades the
   * sending reputation dues receipts also depend on.
   */
  undeliverable?: boolean
}

/**
 * Applies the captain's audience choice to the SubKrewe's current membership.
 *
 * Runs on the server against a freshly-read member list, so a stale composer
 * can never widen the audience or reach someone who has since left. For
 * `custom`, the client's ids are treated as a filter over that same list —
 * never as a recipient list in their own right.
 */
export function selectSubkreweEmailRecipients(
  candidates: SubkreweEmailCandidate[],
  audience: SubkreweEmailAudience,
  memberIds?: string[],
): SubkreweEmailCandidate[] {
  const withEmail = candidates.filter(
    candidate => Boolean(candidate.email?.trim()) && !candidate.undeliverable,
  )

  switch (audience) {
    case 'all':
      return withEmail
    case 'members':
      return withEmail.filter(candidate => (candidate.member_type ?? 'member') === 'member')
    case 'redshirts':
      return withEmail.filter(candidate => candidate.member_type === 'redshirt')
    case 'captains':
      return withEmail.filter(candidate => candidate.member_type === 'captain')
    case 'dues_unpaid':
      return withEmail.filter(candidate => candidate.shouldPayDues)
    case 'custom': {
      const selected = new Set(memberIds ?? [])
      return withEmail.filter(candidate => selected.has(candidate.id))
    }
    default:
      return []
  }
}

/** Human-readable label for an audience, used in the composer and the log. */
export const SUBKREWE_EMAIL_AUDIENCE_LABELS: Record<SubkreweEmailAudience, string> = {
  all: 'Everyone in the SubKrewe',
  members: 'Members only',
  redshirts: 'Red Shirts only',
  captains: 'Captains only',
  dues_unpaid: 'Anyone who still owes dues',
  custom: 'Selected members',
}

/** How a caller earned the right to email a SubKrewe. */
export type SubkreweEmailGrant = 'captain' | 'admin' | 'redshirt_admin'

/**
 * Audiences each grant may target.
 *
 * Red Shirt Admins reach Red Shirts and nobody else — the same rule the admin
 * UI already enforces ("Redshirt admins can only manage redshirts", see
 * components/admin/member/AssignmentManager.vue). They keep `custom` so they
 * can mail a subset, but the candidate list is narrowed first by
 * `candidatesForGrant`, so a hand-picked id outside that set can't survive.
 */
export function allowedAudiencesForGrant(grant: SubkreweEmailGrant): SubkreweEmailAudience[] {
  if (grant === 'redshirt_admin') return ['redshirts', 'custom']
  return [...SUBKREWE_EMAIL_AUDIENCES]
}

/**
 * Narrows the SubKrewe's membership to the people a grant may reach at all,
 * before any audience filter runs. Applied on the server, so a redshirt admin
 * posting `audience: 'all'` still cannot reach a non-Red-Shirt.
 */
export function candidatesForGrant<T extends { member_type?: string }>(
  candidates: T[],
  grant: SubkreweEmailGrant,
): T[] {
  if (grant === 'redshirt_admin') return candidates.filter(c => c.member_type === 'redshirt')
  return candidates
}
